import React from 'react';
import { DossierClient, DossierTransfert } from '../utils/types';

interface RejectModalProps {
  isOpen: boolean;
  dossierToReject: DossierClient | null;
  transfertToReject: DossierTransfert | null;
  rejectReason: string;
  onClose: () => void;
  onConfirmReject: () => void;
  onRejectReasonChange: (reason: string) => void;
}

const RejectModal: React.FC<RejectModalProps> = ({
  isOpen,
  dossierToReject,
  transfertToReject,
  rejectReason,
  onClose,
  onConfirmReject,
  onRejectReasonChange
}) => {
  if (!isOpen) return null;

  const target = dossierToReject || transfertToReject;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-md">
      <div className="bg-white rounded-xl sm:rounded-2xl max-w-full sm:max-w-lg w-full shadow-2xl border-2 sm:border-4 border-red-200 mx-2 sm:mx-4">
        <div className="p-4 sm:p-6 border-b-2 sm:border-b-4 border-red-300 bg-gradient-to-r from-red-600 to-rose-600">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mr-3 sm:mr-4">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl sm:text-2xl font-bold text-white truncate">Motif de rejet</h3>
              <p className="text-red-100 text-xs sm:text-sm mt-1 truncate">Dossier: {target?.nom}</p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 bg-gradient-to-br from-red-50 to-white">
          <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Décrivez le motif du rejet *
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => onRejectReasonChange(e.target.value)}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-red-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500/30 focus:border-red-500 min-h-[120px] sm:min-h-[140px] transition-all duration-300 hover:border-red-300 bg-white/80 backdrop-blur text-sm"
            placeholder="Expliquez en détail pourquoi ce dossier est rejeté (documents manquants, non conformes, etc.)..."
            required
          />
          <p className="text-xs text-gray-600 mt-1 sm:mt-2 flex items-center">
            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Le client recevra ce motif par email
          </p>
        </div>

        <div className="p-4 sm:p-6 border-t border-red-100 bg-gray-50 flex justify-end space-x-2 sm:space-x-3">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 bg-white text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-100 border border-gray-300 hover:border-gray-400 transition-all duration-300 font-medium text-sm"
          >
            Annuler
          </button>
          <button
            onClick={onConfirmReject}
            disabled={!rejectReason.trim()}
            className={`px-4 sm:px-6 py-2 rounded-lg sm:rounded-xl font-medium transition-all duration-300 flex items-center text-sm ${
              rejectReason.trim()
                ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-500/50'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Valider le rejet
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectModal;
